import { toggleDeleteSegmentModal, toggleRenameSegmentModal } from "app/actions/conversionModuleActions";
import {
    groupMenuActions,
    getCustomSegmentGroupsWithData,
} from "app/components/SecondaryBar/NavBars/ConversionAnalysisNavBar/utils";
import { SegmentsGroups } from "./NavGroups/SegmentsGroups";
import type { ISegmentData, ISegmentGroupData } from "app/pages/conversion/ConversionSegmentsUtils";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { SwTrack } from "app/services/SwTrack";
import { periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import { swSettings } from "app/common/services/swSettings";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { sidebarTrackingDispatcher } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteNavBarUtils";

const BodyContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 8px;
`;

const CONVERSION_WIZARD_STATE = "conversion-wizard";
const CUSTOM_SEGMENT_STATE = "conversion-customsegement";
const ConversionAnalysisNavBarBody: FC = () => {
    const { params } = useCurrentStateAndParams();
    const dispatch = useDispatch();
    const { segments } = useSelector((state) => state.conversionModule);
    const { gid, sid, country, tab, duration, comparedDuration } = params;

    const DEFAULT_COMPARED_DURATION = useMemo(() => {
        if (swSettings?.current?.resources?.SnapshotInterval?.count > 12) {
            return "12m";
        }
        return "6m";
    }, []);
    const [customSegmentGroups, setCustomSegmentGroups] = useState(getCustomSegmentGroupsWithData(segments));
    const [selectedGroupId, setSelectedGroupId] = useState(gid ? gid : "");
    const [selectedSegmentId, setSelectedSegmentId] = useState(sid ? sid : "");

    useEffect(() => {
        setSelectedGroupId(gid);
    }, [gid]);

    useEffect(() => {
        setSelectedSegmentId(sid);
    }, [sid]);

    useEffect(() => {
        const userSegmentGroupsWithData = getCustomSegmentGroupsWithData(segments);
        setCustomSegmentGroups(userSegmentGroupsWithData);
    }, [segments, setCustomSegmentGroups]);

    const trackInternalLink = (segment?: ISegmentData, group?: ISegmentGroupData) => {
        SwTrack.all.trackEvent(
            "Internal Link",
            "click",
            `Side Bar/Analyze ${segment ? `website` : `group`}/${segment ? segment.domain : group.id}/${
                segment ? segment.id : group.name
            }`,
        );
    };

    const onCreateNewClick = useCallback(() => {
        SwTrack.all.trackEvent("Button", "click", `Side Bar/Add Group`);
        proNavigator.go(CONVERSION_WIZARD_STATE);
    }, []);

    const onAllGroupsClick = () => {
        proNavigator.go("conversion-homepage");
    };

    const onGroupSelect = useCallback(
        (group) => {
            const groupParams = group.id.split(":");
            if ((groupParams && groupParams.length === 2) || groupParams.length === 0) {
                return;
            }
            const stateName = "conversion-customgroup";
            trackInternalLink(undefined, group);
            setSelectedGroupId(group.id);

            const trackingReportProps = {
                depth: 3,
                type: "click",
                report: "Group overview",
                report_group: group.name,
                report_domain: null,
                analytics_domain: "Research/buyerJourney",
                position_order: 1,
                is_dropdown_list: false,
                is_locked: false,
                tag: "none",
            };
            sidebarTrackingDispatcher(trackingReportProps);

            proNavigator.go(stateName, {
                gid: group.id,
                country,
                duration,
                comparedDuration: DEFAULT_COMPARED_DURATION,
                tab,
            });
        },
        [customSegmentGroups, country, tab, duration],
    );

    const onSiteSearchNavigation = useCallback(
        (internalLinkParams) => {
            const isSelected = `${gid}:${sid}` === `${internalLinkParams.gid}:${internalLinkParams.sid}`;

            if (isSelected) {
                proNavigator.applyUpdateParams(internalLinkParams);
            } else {
                proNavigator.go(CUSTOM_SEGMENT_STATE, { ...internalLinkParams });
            }
        },
        [gid, sid],
    );

    const onSegmentClick = useCallback(
        (groupId, segment, groupName, idx, isOssClick) => {
            trackInternalLink(segment);
            const isDisabled = !segment.countries.includes(parseInt(country, 10));
            const isPeriodOverPeriodAllowed = periodOverPeriodService._periodOverPeriodEnabled(
                duration,
                comparedDuration || DEFAULT_COMPARED_DURATION,
                [],
                "Conversion",
            );
            const internalLinkParams = {
                sid: segment.id,
                gid: groupId,
                country: !isDisabled ? country : isOssClick ? segment.ossCountries[0] : segment.countries[0],
                duration,
                comparedDuration: isPeriodOverPeriodAllowed ? comparedDuration || DEFAULT_COMPARED_DURATION : undefined,
                tab: isOssClick ? "1" : tab,
            };
            const trackingReportProps = {
                depth: 3,
                type: "click",
                report: segment.id,
                report_group: groupName,
                report_domain: null,
                analytics_domain: "Research/buyerJourney",
                position_order: idx + 2, // because the "group overview" is always the first
                is_dropdown_list: false,
                is_locked: false,
                tag: "none",
            };
            sidebarTrackingDispatcher(trackingReportProps);
            if (isOssClick) {
                onSiteSearchNavigation(internalLinkParams);
            } else {
                proNavigator.go(CUSTOM_SEGMENT_STATE, {
                    ...internalLinkParams,
                    tab,
                });
            }
        },
        [customSegmentGroups, tab, duration, country, comparedDuration],
    );

    const handleMenuToggle = () => {
        SwTrack.all.trackEvent("Button", "click", `Side bar/Additional Options/Toggle`);
    };

    const handleMenuItemClick = (gid, country, action) => {
        switch (action) {
            case groupMenuActions.DELETE:
                return () => {
                    SwTrack.all.trackEvent("Button", "click", `Side bar/Delete group/${gid}`);
                    dispatch(toggleDeleteSegmentModal(true, gid));
                };
            case groupMenuActions.RENAME:
                return () => {
                    SwTrack.all.trackEvent("Button", "click", `Side bar/Rename group/${gid}`);
                    dispatch(toggleRenameSegmentModal(true, gid));
                };
            case groupMenuActions.ADD:
            case groupMenuActions.REMOVE:
                return () => {
                    SwTrack.all.trackEvent("Button", "click", `Side bar/${action} sites/${gid}`);
                    proNavigator.go(CONVERSION_WIZARD_STATE, { gid, country, duration });
                };
            default:
                return;
        }
    };

    return (
        <BodyContainer>
            <SegmentsGroups
                country={country}
                segments={segments}
                groups={customSegmentGroups}
                onGroupClick={onGroupSelect}
                onSegmentClick={onSegmentClick}
                onAllGroupsClick={onAllGroupsClick}
                onCreateNewClick={onCreateNewClick}
                selectedGroupId={selectedGroupId}
                selectedSegmentId={selectedSegmentId}
                onMenuToggle={handleMenuToggle}
                handleMenuItemClick={handleMenuItemClick}
            />
        </BodyContainer>
    );
};

export default ConversionAnalysisNavBarBody;
